"use client";

import { motion } from "framer-motion";
import { Autoplay, EffectCoverflow } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRef, useEffect } from "react";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css";
import "swiper/css/effect-cards";

import Link from "next/link";
import { Button } from "@/components/ui/button";

interface BoundingBox {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
  label?: string;
}

interface ImageWithBoxes {
  src: string;
  alt: string;
  boxes: BoundingBox[];
}

const uniqueImages: ImageWithBoxes[] = [
  {
    src: "/images/solutions/wildlife-detection/coyote_example.jpg",
    alt: "coyote in trail camera",
    boxes: [
      {
        x_min: 0.3985451050182538,
        y_min: 0.4862024552427533,
        x_max: 0.5965673094489994,
        y_max: 0.7024583463212741,
        label: "coyote"
      }
    ],
  },
  {
    src: "/images/solutions/satellite-imagery/satellite_airport Large.jpeg",
    alt: "satellite view of airport",
    boxes: [
      {
        x_min: 0.15123327706126458,
        y_min: 0.4935291113734217,
        x_max: 0.34925548149201013,
        y_max: 0.8554444956647014,
        label: "airport"
      }
    ],
  },
  {
    src: "/images/solutions/robot-vision/robot_laundry_example.jpg",
    alt: "robot view of laundry",
    boxes: [
      {
        x_min: 0.3959978563299161,
        y_min: 0.6305546994754534,
        x_max: 0.5209131896133878,
        y_max: 0.7418793181198544,
        label: "dirty laundry"
      },
      {
        x_min: 0.5072101051686715,
        y_min: 0.8161747350148886,
        x_max: 0.7264164832966267,
        y_max: 0.9902768778883372,
        label: "dirty laundry"
      },
      {
        x_min: 0.024318775288883,
        y_min: 0.7399470321459622,
        x_max: 0.2044202276436683,
        y_max: 0.8631810225949956,
        label: "dirty laundry"
      }
    ],
  },
  {
    src: "/images/solutions/satellite-imagery/satellite_smoke_revised.jpeg",
    alt: "satellite detection of smoke",
    boxes: [
      {
        x_min: 0.23102908381791523,
        y_min: 0.11304750315784512,
        x_max: 0.3926268301605794,
        y_max: 0.45586745285388514,
        label: "smoke"
      }
    ],
  },
  {
    src: "/images/solutions/robot-vision/robot_dishes_example.jpg",
    alt: "robot dishes detection",
    boxes: [
      {
        x_min: 0.4222182512910134,
        y_min: 0.5794431174211956,
        x_max: 0.7038814554538547,
        y_max: 0.9063828845338386,
        label: "dirty dishes"
      }
    ],
  },
];

// Component to render image with bounding boxes
function ImageWithBoxes({ src, boxes }: ImageWithBoxes) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas || !container) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;
      
      // Fixed dimensions for swiper slide
      const targetWidth = 500;
      const targetHeight = 420;
      
      // Calculate how to fit the image within target dimensions while maintaining aspect ratio
      const imgAspect = imgElement.width / imgElement.height;
      const targetAspect = targetWidth / targetHeight;
      
      let drawWidth, drawHeight, offsetX, offsetY;
      
      if (imgAspect > targetAspect) {
        // Image is wider - fit to height
        drawHeight = targetHeight;
        drawWidth = targetHeight * imgAspect;
        offsetX = (targetWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        // Image is taller - fit to width
        drawWidth = targetWidth;
        drawHeight = targetWidth / imgAspect;
        offsetX = 0;
        offsetY = (targetHeight - drawHeight) / 2;
      }

      canvas.width = targetWidth * 2;
      canvas.height = targetHeight * 2;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      // Draw image centered and covering the area
      ctx.drawImage(imgElement, offsetX, offsetY, drawWidth, drawHeight);

      // Draw bounding boxes (coordinates are relative to the actual image)
      const boxColor = "rgb(34, 197, 94)";
      const transparentColor = "rgba(34, 197, 94, 0.25)";

      boxes.forEach((box) => {
        const x = offsetX + (box.x_min * drawWidth);
        const y = offsetY + (box.y_min * drawHeight);
        const width = (box.x_max - box.x_min) * drawWidth;
        const height = (box.y_max - box.y_min) * drawHeight;

        ctx.fillStyle = transparentColor;
        ctx.fillRect(x, y, width, height);

        ctx.strokeStyle = boxColor;
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, width, height);

        // Draw label if present
        if (box.label) {
          ctx.font = "bold 12px system-ui, -apple-system, sans-serif";
          const textMetrics = ctx.measureText(box.label);
          const textWidth = textMetrics.width;
          const textHeight = 12;
          const padding = 4;
          
          // Position label above the box
          const labelX = x;
          const labelY = Math.max(y - 6, textHeight + padding);
          
          // Draw label background
          ctx.fillStyle = boxColor;
          ctx.fillRect(labelX, labelY - textHeight - padding, textWidth + padding * 2, textHeight + padding * 2);
          
          // Draw label text
          ctx.fillStyle = "white";
          ctx.fillText(box.label, labelX + padding, labelY - padding);
        }
      });
    };

    imgElement.src = src;

    return () => {
      isMounted = false;
    };
  }, [src, boxes]);

  return (
    <div ref={containerRef} className="h-full w-full">
      <canvas
        ref={canvasRef}
        className="h-full w-full overflow-hidden rounded-3xl object-cover shadow-lg"
        style={{ width: '500px', height: '420px' }}
      />
    </div>
  );
}

// Duplicate for smooth looping with coverflow effect
const images = [...uniqueImages, ...uniqueImages];

const css = `
  .mySwiperHero210 {
    width: 100%;
    height: 420px;
    padding-bottom: 50px;
  }
  
  .mySwiperHero210 .swiper-slide {
    background-position: center;
    background-size: cover;
    width: 500px;
  }
  
  .mySwiperHero210 .swiper-slide img {
    display: block;
    width: 100%;
  }
  
  .swiper-3d .swiper-slide-shadow-left {
    background-image: none;
  }
    
  .swiper-3d .swiper-slide-shadow-right{
    background: none;
  }
`;

export default function DetectHero() {
  return (
    <section className="pt-8 sm:pt-20">
      <style>{css}</style>
      <div className="container mx-auto px-0 sm:px-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-4 px-8 sm:px-0">
          <h1
            className="text-center text-5xl lg:text-7xl tracking-[-0.01em]"
            style={{ fontFamily: 'var(--font-instrument-serif)' }}
          >
            Open&#8209;Vocabulary Object Detection
          </h1>
          <p className="text-muted-foreground text-center lg:text-lg">
            Moondream Object Detection understands natural language to locate any object in your images. Fast, accurate bounding boxes powered by visual understanding.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-full px-6 py-3">
              <Link href="/playground?skill=detect">Try Object Detection</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="rounded-full px-6 py-3"
            >
              <a
                href="https://docs.moondream.ai/skills/detect"
                target="_blank"
                rel="noreferrer"
              >
                View docs
              </a>
            </Button>
          </div>
        </div>

        <div className="relative mt-12 h-[420px] w-full lg:px-20">
          <div className="from-background md:w-58 sm:via-background absolute left-0 z-10 h-full w-12 bg-gradient-to-r to-transparent lg:left-16" />
          <div className="from-background md:w-58 sm:via-background absolute right-0 z-10 h-full w-12 bg-gradient-to-l to-transparent lg:right-16" />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <Swiper
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              effect="coverflow"
              grabCursor
              slidesPerView="auto"
              centeredSlides
              loop
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: true,
              }}
              className="mySwiperHero210"
              modules={[EffectCoverflow, Autoplay]}
            >
              {images.map((image, index) => (
                <SwiperSlide key={index}>
                  <ImageWithBoxes
                    src={image.src}
                    alt={image.alt}
                    boxes={image.boxes}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </div>
    </section >
  );
}

